import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { ProtectedRoute } from './auth/ProtectedRoute';
import { PublicRoute } from './auth/PublicRoute';
import { DomainBoundary } from './routing/DomainBoundary';
import { domains } from './routing/domains';
import DashboardLayout from './components/DashboardLayout';
import EnvironmentBanner from './components/EnvironmentBanner';
import { ScrollToTop } from './components/ScrollToTop';
import { useMaintenanceMode } from './features/maintenance/useMaintenanceMode';

// Lazy-loaded feature pages
const LandingPage = lazy(() => import('./features/landing/LandingPage'));
const AboutPage = lazy(() => import('./features/about/AboutPage'));
const ContactPage = lazy(() => import('./features/contact/ContactPage'));
const TermsPage = lazy(() => import('./features/legal/TermsPage'));
const PrivacyPage = lazy(() => import('./features/legal/PrivacyPage'));
const RiskDisclaimerPage = lazy(() => import('./features/legal/RiskDisclaimerPage'));
const LoginPage = lazy(() => import('./features/auth/LoginPage'));
const SignUpPage = lazy(() => import('./features/auth/SignUpPage'));
const DashboardHome = lazy(() => import('./features/dashboard/DashboardHome'));
const PortfolioOverview = lazy(() => import('./features/portfolio/PortfolioOverview'));
const InstrumentDetailPage = lazy(() => import('./features/instruments/InstrumentDetailPage'));
const ExecutionForOrderPage = lazy(() => import('./features/executions/ExecutionForOrderPage'));
const ExecutionDetailPage = lazy(() => import('./features/executions/ExecutionDetailPage'));
const TradeHistory = lazy(() => import('./features/trades/TradeHistory'));
const ManualTradeForm = lazy(() => import('./features/trades/ManualTradeForm'));
const TradePage = lazy(() => import('./features/trades/TradePage'));
const NotificationPrefs = lazy(() => import('./features/notifications/NotificationPrefs'));
const NotificationFeed = lazy(() => import('./features/notifications/NotificationFeed'));
const DailyReportSettings = lazy(() => import('./features/notifications/DailyReportSettings'));
const PendingApprovals = lazy(() => import('./features/approvals/PendingApprovals'));
const PositionManagement = lazy(() => import('./features/positions/PositionManagement'));
const ConfigSettings = lazy(() => import('./features/config/ConfigSettings'));
const AssistantPersonalisationPage = lazy(() => import('./features/profile/AssistantPersonalisationPage'));
const ProfilePage = lazy(() => import('./features/profile/ProfilePage'));
const ChangePassword = lazy(() => import('./features/profile/ChangePassword'));
const MfaSetup = lazy(() => import('./features/profile/MfaSetup'));
const ForgotPassword = lazy(() => import('./features/profile/ForgotPassword'));
const BrokerList = lazy(() => import('./features/brokers/BrokerList'));
const ConnectBrokerForm = lazy(() => import('./features/brokers/ConnectBrokerForm'));
const GettingStarted = lazy(() => import('./features/guide/GettingStarted'));
const HelpSection = lazy(() => import('./features/guide/HelpSection'));
const MyStrategiesPage = lazy(() => import('./features/strategies/MyStrategiesPage'));
const StrategyForm = lazy(() => import('./features/strategies/StrategyForm'));
const PlatformMessagesPage = lazy(() => import('./features/messages/PlatformMessagesPage'));
const AICreditUsagePage = lazy(() => import('./features/billing/AICreditUsagePage'));
const BillingPage = lazy(() => import('./features/billing/BillingPage'));
const StatusPage = lazy(() => import('./features/status/StatusPage'));
import MaintenancePage from './features/maintenance/MaintenancePage';

function PageLoader() {
  return (
    <div className="flex items-center justify-center p-12">
      <p className="text-gray-500">Loading…</p>
    </div>
  );
}

function NotFound() {
  return <div className="p-6"><h1 className="text-xl">404 — Page not found</h1></div>;
}

export default function App() {
  const { isMaintenanceMode, isLoading } = useMaintenanceMode();

  if (isLoading) {
    return (
      <div data-testid="maintenance-check-loading" className="min-h-screen bg-[#07080a] flex items-center justify-center">
        <PageLoader />
      </div>
    );
  }

  if (isMaintenanceMode) {
    return (
      <DomainBoundary>
        <ScrollToTop />
        <MaintenancePage />
      </DomainBoundary>
    );
  }

  return (
    <DomainBoundary>
      <ScrollToTop />
      {domains().isDashboard && <EnvironmentBanner />}
      <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* The public home remains readable even when the visitor is signed in. */}
          {!domains().isDashboard && <Route path="/" element={<LandingPage />} />}
          {!domains().isDashboard && <Route path="/about" element={<AboutPage />} />}
          {!domains().isDashboard && <Route path="/contact" element={<ContactPage />} />}
          {!domains().isDashboard && <Route path="/terms" element={<TermsPage />} />}
          {!domains().isDashboard && <Route path="/privacy" element={<PrivacyPage />} />}
          {!domains().isDashboard && <Route path="/risk-disclaimer" element={<RiskDisclaimerPage />} />}
          {!domains().isDashboard && <Route path="/disclaimer" element={<Navigate to="/risk-disclaimer" replace />} />}

          {/* Public routes (no layout shell) */}
          <Route path="/login" element={<PublicRoute redirectTo="/dashboard"><LoginPage /></PublicRoute>} />
          <Route path="/signup" element={<PublicRoute redirectTo="/dashboard"><SignUpPage /></PublicRoute>} />
          <Route path="/forgot-password" element={<PublicRoute redirectTo="/dashboard"><ForgotPassword /></PublicRoute>} />
          <Route path="/status" element={<StatusPage />} />
          <Route path="/health" element={<Navigate to="/status" replace />} />

          {/* Protected routes with dashboard layout */}
          <Route element={<ProtectedRoute />}>
            <Route element={<DashboardLayout />}>
              {domains().isDashboard && <Route path="/" element={<DashboardHome />} />}
              <Route path="/dashboard" element={<DashboardHome />} />
              <Route path="/portfolio" element={<PortfolioOverview />} />
              <Route path="/instruments/:symbol" element={<InstrumentDetailPage />} />
              <Route path="/trade-executions/order/:orderId" element={<ExecutionForOrderPage />} />
              <Route path="/trade-executions/:executionId" element={<ExecutionDetailPage />} />
              <Route path="/trades" element={<TradeHistory />} />
              <Route path="/trades/new" element={<ManualTradeForm />} />
              <Route path="/trade" element={<TradePage />} />
              <Route path="/notifications" element={<NotificationFeed />} />
              <Route path="/notifications/preferences" element={<NotificationPrefs />} />
              <Route path="/notifications/daily-report" element={<DailyReportSettings />} />
              <Route path="/messages" element={<PlatformMessagesPage />} />
              <Route path="/approvals" element={<PendingApprovals />} />
              <Route path="/positions" element={<PositionManagement />} />
              <Route path="/config" element={<ConfigSettings />} />
              {import.meta.env.VITE_BILLING_UI_ENABLED === 'true' && <Route path="/billing" element={<BillingPage />} />}
              <Route path="/ai-credit-usage" element={<AICreditUsagePage />} />
              <Route path="/profile" element={<ProfilePage />} />
              <Route path="/profile/assistant-personalisation" element={<AssistantPersonalisationPage />} />
              <Route path="/profile/change-password" element={<ChangePassword />} />
              <Route path="/profile/mfa" element={<MfaSetup />} />
              <Route path="/brokers" element={<BrokerList />} />
              <Route path="/brokers/connect" element={<ConnectBrokerForm />} />
              <Route path="/brokers/:brokerId/update" element={<ConnectBrokerForm />} />
              <Route path="/strategies" element={<MyStrategiesPage />} />
              <Route path="/strategies/create" element={<StrategyForm />} />
              <Route path="/strategies/:id/edit" element={<StrategyForm />} />
              <Route path="/indicators" element={<Navigate to="/strategies?type=indicator" replace />} />
              <Route path="/guide" element={<GettingStarted />} />
              <Route path="/help" element={<HelpSection />} />
            </Route>
          </Route>

          {/* Fallback */}
          <Route path="/404" element={<NotFound />} />
          <Route path="*" element={<Navigate to="/404" replace />} />
        </Routes>
      </Suspense>
    </DomainBoundary>
  );
}
